import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import type { StoreCode } from '@familyhub/shared';
import { db } from '../db';
import { saveCategoryOrder } from '../list/actions';
import { spliceAisleOrder } from '../list/aisle-order';

/**
 * Per-store aisle order (ARCHITECTURE §6.5).
 *
 * **Categories are reordered, not products.** Sorting ~25 categories once per
 * store takes ten minutes; sorting hundreds of products never finishes, and a
 * newly bought product would land unsorted. With categories it already appears
 * in the right aisle, because it inherits its category's position.
 *
 * The screen has **two scopes**, because the order is set in two very different
 * moments:
 *
 *   - **all** — sitting down to lay a shop out end to end. Every category the
 *     household buys, in one list. Reached from Status, and the default.
 *   - **list** — standing in the aisle with the order wrong. Only the categories
 *     that are on the list for this store, so the handful that matter are right
 *     there instead of buried in the full taxonomy. Reordering them splices back
 *     into the full order without moving anything not on the list
 *     (`spliceAisleOrder`). Reached from the list group's header (`?scope=list`).
 *
 * Either way, only categories the household actually buys are ever listed —
 * presenting a full taxonomy to sort is a chore nobody completes. New ones
 * append at the bottom until moved.
 *
 * The move buttons are the accessible equivalent of the drag the design calls
 * for: same operation, same result, and it works with a screen reader and with
 * one thumb on a moving train.
 */

type Scope = 'all' | 'list';

export function AisleOrderScreen() {
  const { storeCode } = useParams<{ storeCode: string }>();
  const [params] = useSearchParams();
  const [scope, setScope] = useState<Scope>(params.get('scope') === 'list' ? 'list' : 'all');
  const [order, setOrder] = useState<string[] | null>(null);
  const [saved, setSaved] = useState(false);

  /**
   * Whether this order is still sitting in the outbox.
   *
   * The button used to say "Saved" the instant the mutations were queued, which
   * is true of the phone and not of the household: with no signal the other
   * phone has not seen a thing. Saying "saved" for both states is a small lie
   * about the one thing this screen exists to share.
   */
  const unsent = useLiveQuery(
    async () =>
      db.outbox
        .filter((entry) => entry.mutation.op === 'upsert' && entry.mutation.table === 'store_category_order')
        .count(),
    [],
    0,
  );

  const store = useLiveQuery(
    () => (storeCode ? db.stores.where('code').equals(storeCode).first() : undefined),
    [storeCode],
  );

  /**
   * The full order (every category in use, positioned then name-ordered) and
   * the set that is on the list for this store. Both come from one load: `known`
   * is what the splice fills, `onList` is what "list" scope shows.
   */
  const data = useLiveQuery(async () => {
    if (!store) return null;
    const [generics, positions, items] = await Promise.all([
      db.generic_products.toArray(),
      db.store_category_order.where('store_id').equals(store.id).toArray(),
      db.list_items.where('store_id').equals(store.id).toArray(),
    ]);

    const used = new Set<string>();
    for (const g of generics) {
      if (!g.deleted_at && g.category) used.add(g.category);
    }
    for (const p of positions) used.add(p.category);

    const position = new Map(positions.map((p) => [p.category, p.position]));
    const known = [...used].sort((a, b) => {
      const pa = position.get(a);
      const pb = position.get(b);
      // Unpositioned categories append at the bottom, in name order.
      if (pa === undefined && pb === undefined) return a.localeCompare(b);
      if (pa === undefined) return 1;
      if (pb === undefined) return -1;
      return pa - pb;
    });

    const categoryOf = new Map(generics.map((g) => [g.id, g.category]));
    const onList = new Set<string>();
    for (const item of items) {
      if (item.deleted_at) continue;
      const category = categoryOf.get(item.generic_product_id);
      if (category) onList.add(category);
    }

    return { known, onList };
  }, [store?.id]);

  /** What the current scope actually edits. */
  const scoped = useMemo(() => {
    if (!data) return null;
    return scope === 'all' ? data.known : data.known.filter((c) => data.onList.has(c));
  }, [data, scope]);

  useEffect(() => {
    if (scoped && order === null) setOrder(scoped);
  }, [scoped, order]);

  function switchScope(next: Scope) {
    if (next === scope) return;
    setScope(next);
    // Re-initialise from the new scope's slice. Any unsaved moves go with it —
    // cheap to redo, and a save flushes immediately, so this is rarely material.
    setOrder(null);
    setSaved(false);
  }

  function move(index: number, delta: number) {
    setOrder((current) => {
      if (!current) return current;
      const target = index + delta;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      const [moved] = next.splice(index, 1);
      next.splice(target, 0, moved!);
      return next;
    });
    setSaved(false);
  }

  async function save() {
    if (!store || !order || !data) return;
    // The whole order is always what is saved: PUT semantics (API §5.1), and the
    // sync path replaces it wholesale. In "list" scope `order` is only the subset
    // on the list, so it is first spliced back into the full order — untouched
    // categories keep their slot. In "all" scope `order` already is the full order.
    const full = scope === 'all' ? order : spliceAisleOrder(data.known, order);
    await saveCategoryOrder(store.id, full);
    setSaved(true);
  }

  const saveLabel = !saved
    ? 'Volgorde opslaan'
    : unsent > 0
      // Not a failure and not a warning — the order is safe and will go. It
      // just has not reached the other phone yet, and that is worth knowing
      // before somebody wonders why their partner's list is in the old order.
      ? 'Opgeslagen op deze telefoon'
      : 'Opgeslagen';

  return (
    <>
      <header className="topbar">
        <Link className="back btn btn-quiet" to="/boodschappen">
          ‹ Terug
        </Link>
        <h1>Schapvolgorde {store?.name ?? (storeCode as StoreCode)}</h1>
      </header>

      <main className="stack">
        <nav className="chips" aria-label="Welke categorieën">
          <button className="chip" aria-pressed={scope === 'list'} onClick={() => switchScope('list')}>
            Op de lijst
          </button>
          <button className="chip" aria-pressed={scope === 'all'} onClick={() => switchScope('all')}>
            Alle categorieën
          </button>
        </nav>

        <p className="row-sub">
          {scope === 'list'
            ? 'De categorieën van je lijst, in de volgorde waarin je door de winkel loopt. De rest blijft staan.'
            : 'Alle categorieën in die volgorde — geldt ook voor wat je voor het eerst koopt.'}
        </p>

        {order && order.length === 0 && scope === 'list' && (
          <p className="empty">
            Niets op je lijst voor deze winkel om te ordenen.
            <br />
            <button className="btn btn-quiet" onClick={() => switchScope('all')}>
              Alle categorieën
            </button>
          </p>
        )}

        {order && order.length === 0 && scope === 'all' && (
          <p className="empty">
            Nog geen categorieën. Die verschijnen zodra producten ingedeeld zijn — koop eerst
            een paar dingen.
          </p>
        )}

        {order && order.length > 0 && (
          <div className="card">
            {order.map((category, index) => (
              <div className="order-row" key={category}>
                <span className="name">{category}</span>
                <button
                  aria-label={`${category} eerder`}
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                >
                  ↑
                </button>
                <button
                  aria-label={`${category} later`}
                  disabled={index === order.length - 1}
                  onClick={() => move(index, 1)}
                >
                  ↓
                </button>
              </div>
            ))}
          </div>
        )}

        {saved && unsent > 0 && (
          <p className="row-sub">
            Zodra deze telefoon verbinding heeft, komt het op de andere telefoon aan.
          </p>
        )}

        {order && order.length > 0 && (
          <button className="btn btn-primary" onClick={() => void save()}>
            {saveLabel}
          </button>
        )}
      </main>
    </>
  );
}
